import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { List, Map } from 'immutable'
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import compose from 'recompose/compose'
import { connect as connectRedux, useSelector } from 'react-redux'
import FormsyForm from 'formsy-react'
import { useQuery } from '@apollo/client'
import _get from 'lodash/get'
import { connect as connectPage, PAGE_SEO_TYPE_LOCATION } from '../../Page/connect'
import { getBoundActions } from '../../../actions/index'
import {
  getEmailCustomerServiceUrl,
  getTechnicalPlaybackIssuesUrl,
  getTechnicalOfflineDownloadUrl,
  getTechnicalSetupUrl,
  getTechnicalTroubleFindingContentUrl,
  getTechnicalHelpUrl,
} from '../../../services/url/index'
import Link from '../../Link/index'
import { FR, DE, EN } from '../../../services/languages/constants'
import { TARGET_BLANK } from '../../Link/constants'
import { getPrimary } from '../../../services/languages/index'
import {
  Select, Textarea,
} from '../../FormInput.v2/index'
import Sherpa, { TYPE_SMALL_WHITE } from '../../Sherpa/index'
import { Card } from '../../Card/index'
import { historyRedirect } from '../../../services/navigation/index'
import { requestAnimationFrame } from '../../../services/animate/index'
import {
  SCHEMA_USER_ACCOUNT_CANCEL_V2,
  TEXTBOX_CANCEL_REASON_OTHER_ANSWER,
  shouldRenderCancelOffer,
  shouldRenderCancelPriceIncreaseOffer,
  hasScheduledOrActivePause,
  userPauseAllowed,
} from '../../../services/user-account/index'
import { selectHasActiveGiftSubscription } from '../../../services/user-account/selectors'
import AccountCancelOffer from '../AccountCancelOffer/index'
import AccountCancelDissatisfied from '../AccountCancelDissatisfied/index'
import {
  URL_ACCOUNT_CANCEL_OFFER,
  URL_ACCOUNT_CANCEL_CONFIRM,
  URL_PLAYLIST_PURCHASES_VIDEOS,
  URL_ACCOUNT_CANCEL_SPECIAL_OFFER,
} from '../../../services/url/constants'
import {
  getPlanSubscriptionType,
  PLAN_SUBSCRIPTION_MONTHLY,
} from '../../../services/plans/index'
import { getAuthIsLoggedIn } from '../../../services/auth/index'
import { GET_USER_SKU_MAPPINGS, PROVIDER_GAIA_BONUS } from '../../../services/playlist/query'
import { hasContentRelated } from '../../../services/playlist/index'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../services/apollo-graphql/index'
import { H2, H1, HEADING_TYPES } from '../../Heading/index'
import Button, { TYPES } from '../../Button/index'
import { CANCEL_FLOW_REASON_SELECTED_GA4_EVENT } from '../../../services/event-tracking/index'
import { scrollToTop } from '../../../services/dom/index'
import { TYPE_MY_ACCOUNT_CANCEL_MEMBERSHIP } from '../../../services/dialog/index'
import { getCancelSpecialOfferSkusFor1g } from '../AccountCancelSpecialOffer/utils'
import { DE as COUNTRY_DE } from '../../../services/country/index'

const CANCEL_REASON_SELECT = 'cancelReasonSelect'
const CANCEL_REASON_RADIOS = 'cancelReasonRadios'
const TOO_EXPENSIVE = 'too expensive'
const UNSATISFIED_CONTENT = 'unsatisfied with content'
const DONT_HAVE_TIME = 'i do not have time'
const COMPLETED_WHAT_I_WANTED = 'i have completed what i wanted'
const TECHNICAL_ISSUES = 'technical issues'
const OTHER = 'other'
const COMING_BACK_LATER = 'coming back later'

function AccountCancelPage (props) {
  const {
    auth,
    user,
    staticText,
    userLanguage,
    cancelReason,
    userAccount,
    subscription,
    setUserAccountCancelReason,
    setUserAccountCancelOfferShown,
    setUserAccountCancel,
    setUserAccountCancelSpecialOfferSkus,
    setEventDefault,
    renderModal,
    history,
  } = props
  const userPrimaryLanguage = getPrimary(userLanguage)
  const freeOfferEligible = shouldRenderCancelOffer(props)

  const currentPlanIsGift = useSelector(selectHasActiveGiftSubscription)
  const userCanPause = userPauseAllowed(props) && !hasScheduledOrActivePause(userAccount)

  // Check if user has Gaia Bonus eligibility (same pattern as MyLibraryPage)
  const {
    data: skuMappingsData,
    loading: skuMappingsLoading,
  } = useQuery(GET_USER_SKU_MAPPINGS, {
    variables: {
      language: [userPrimaryLanguage],
    },
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    skip: !getAuthIsLoggedIn(auth),
    context: {
      jwt: auth.get('jwt'),
    },
  })

  const bonusMappings = _get(skuMappingsData, ['ecommerceSkuMappingsList', 'ecommerceSkuMappings'], []) || []
  const hasBonus = hasContentRelated(bonusMappings, PROVIDER_GAIA_BONUS)

  const userPlan = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'productRatePlanId'])
  const userPlanType = getPlanSubscriptionType(userPlan)
  const userAccountProcessing = userAccount.get('billingSubscriptionsProcessing')
  const dataReady = !userAccountProcessing && userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map()).size > 0
    && userAccount.getIn(['details', 'data', 'billing', 'payments'], Map()).size > 0
  const subscriptionProcessing = subscription.get('processing')
  const subscriptionSuccess = subscription.get('success')
  const subscriptionError = subscription.get('errors')
  const [otherTextTooLong, setOtherTextTooLong] = useState(false)

  const nextDisabled = !cancelReason
    || otherTextTooLong
    || subscriptionProcessing
    || subscriptionSuccess
    || false

  // Internal state for the next button
  const [showAiGuideFromTechIssues, setShowAiGuideFromTechIssues] = useState(false)

  // Scroll to top on mount
  useEffect(() => {
    scrollToTop()
  }, [])

  const setGa4CancelFlowReasonSelected = (reason, comment) => {
    const eventData = CANCEL_FLOW_REASON_SELECTED_GA4_EVENT.mergeDeep({ reason, comment })
    setEventDefault(eventData)
  }

  const onChange = (value) => {
    setUserAccountCancelReason(value)

    switch (value) {
      case TOO_EXPENSIVE:
        if (freeOfferEligible && userPlanType !== PLAN_SUBSCRIPTION_MONTHLY) {
          setUserAccountCancelOfferShown(true)
        }
        break
      default:
        break
    }
  }

  // onChange for the select box
  const onChangeSelect = (value) => {
    setShowAiGuideFromTechIssues(false)
    onChange(value)
  }

  const onChangeTextarea = (value) => {
    if (value.length > 1000) {
      setOtherTextTooLong(true)
    } else if (otherTextTooLong && value.length <= 1000) {
      setOtherTextTooLong(false)
    }
  }

  const onValidSubmit = (model) => {
    const { setUserAccountCancelFormData } = props
    const billingAccountId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'billingAccountId'], '')
    const subscriptionId = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'subscriptionId'], '')
    const value = model[CANCEL_REASON_RADIOS] || model[CANCEL_REASON_SELECT]
    const comment = value === OTHER ? model[TEXTBOX_CANCEL_REASON_OTHER_ANSWER] : ''

    if (value) {
      const cancelData = _assign(_cloneDeep(SCHEMA_USER_ACCOUNT_CANCEL_V2), {
        billingAccountId,
        subscriptionId,
        reason: value,
        comment,
      })

      // GA4 event
      setGa4CancelFlowReasonSelected(value, comment)

      // Send form data
      setUserAccountCancelFormData(cancelData)
    }

    if (window.scrollTo) {
      requestAnimationFrame(() => window.scrollTo(0, 0))
    }
  }

  // render Too Expensive (1G: Price isn't right shows nothing)
  const renderTooExpensive = () => null

  const renderTechnicalIssues = () => {
    const renderAiGuide = userPrimaryLanguage !== DE

    if (showAiGuideFromTechIssues) {
      return renderDissatisfied()
    }

    if (userPrimaryLanguage !== FR) {
      return (
        <Card className="account-cancel__card">
          <H2 className="account-cancel__subtitle">{staticText.get('weAreHereToHelp')}</H2>
          <p className="account-cancel__text">
            {staticText.get('addressIssues')}
          </p>
          <ul className="account-cancel__list">
            <li className="account-cancel__list-item">
              <Link
                to={getTechnicalPlaybackIssuesUrl(userPrimaryLanguage)}
                directLink
                target={TARGET_BLANK}
                className="account-cancel__list-link"
              >
                {staticText.get('experiencingPlaybackIssues')}
              </Link>
            </li>
            <li className="account-cancel__list-item">
              <Link
                to={getTechnicalOfflineDownloadUrl(userPrimaryLanguage)}
                directLink
                target={TARGET_BLANK}
                className="account-cancel__list-link"
              >
                {staticText.get('howToDownload')}
              </Link>
            </li>
            <li className="account-cancel__list-item">
              <Link
                to={getTechnicalSetupUrl(userPrimaryLanguage)}
                directLink
                target={TARGET_BLANK}
                className="account-cancel__list-link"
              >
                {staticText.get('setUpTVApp')}
              </Link>
            </li>
            <li className="account-cancel__list-item">
              {
                renderAiGuide ? (
                  <Button
                    type={TYPES.LINK}
                    className="account-cancel__list-link"
                    onClick={() => setShowAiGuideFromTechIssues(true)}
                  >
                    {staticText.get('troubleFindingContent')}
                  </Button>
                ) : (
                  <Link
                    to={getTechnicalTroubleFindingContentUrl(userPrimaryLanguage)}
                    directLink
                    target={TARGET_BLANK}
                    className="account-cancel__list-link"
                  >
                    {staticText.get('troubleFindingContent')}
                  </Link>
                )
              }
            </li>
          </ul>
          <p className="account-cancel__text account-cancel__text--tech-info">
            {`${staticText.get('technicalInfo1')} `}
            <Link
              to={getTechnicalHelpUrl(userPrimaryLanguage)}
              directLink
              target={TARGET_BLANK}
              className="account-cancel__text-link"
            >
              {staticText.get('here')}
            </Link>
            {` ${staticText.get('technicalInfo2')} `}
            <Link
              to={getEmailCustomerServiceUrl(userPrimaryLanguage)}
              directLink
              target={TARGET_BLANK}
              className="account-cancel__text-link"
            >
              {staticText.get('here')}
            </Link>
            {` ${staticText.get('technicalInfo3')}`}
          </p>
          <p className="account-cancel__text account-cancel__text--bold">
            {staticText.get('didYouKnow')}
          </p>
          <div className="account-cancel__app-logos">
            {renderAppLogos()}
          </div>
        </Card>
      )
    }

    return null
  }

  const renderAppLogos = () => {
    const baseClass = 'account-cancel__app-logo'
    return (
      <React.Fragment>
        <div className={`${baseClass} ${baseClass}--roku`} />
        {userPrimaryLanguage !== DE
          ? <div className={`${baseClass} ${baseClass}--firetv`} />
          : null
        }
        <div className={`${baseClass} ${baseClass}--chromecast`} />
        <div className={`${baseClass} ${baseClass}--appletv`} />
        <div className={`${baseClass} ${baseClass}--androidtv`} />
      </React.Fragment>
    )
  }

  const renderOther = () => {
    return (
      <Card className="account-cancel__card">
        <div className="account-cancel__other-title">
          {staticText.get('tellUsMore')}
        </div>
        <p className="account-cancel__other-text">
          {staticText.get('howToImprove')}
        </p>
        <Textarea
          label={''}
          value={''}
          className="account-cancel__textarea"
          autocomplete="off"
          name={TEXTBOX_CANCEL_REASON_OTHER_ANSWER}
          validations="maxLength:1000"
          showCharCount
          block
          onChange={onChangeTextarea}
        />
      </Card>
    )
  }

  const renderDissatisfied = () => {
    return (
      <AccountCancelDissatisfied />
    )
  }

  // Render bonus loss info message
  const renderBonusLossInfo = () => {
    if (!hasBonus || skuMappingsLoading) {
      return null
    }

    return (
      <div className="account-cancel__bonus-info">
        {staticText.get('bonusInfoBefore')}
        <Link to={URL_PLAYLIST_PURCHASES_VIDEOS} className="account-cancel__bonus-link">
          {staticText.get('bonusLink')}
        </Link>
        {staticText.get('bonusInfoAfter')}
      </div>
    )
  }

  // render correct selected reason content
  const renderContent = () => {
    let content

    switch (cancelReason) {
      case TOO_EXPENSIVE:
        content = renderTooExpensive()
        break
      case UNSATISFIED_CONTENT:
        content = renderDissatisfied()
        break
      case DONT_HAVE_TIME:
        content = null
        break
      case COMPLETED_WHAT_I_WANTED:
        content = renderDissatisfied()
        break
      case TECHNICAL_ISSUES:
        content = renderTechnicalIssues()
        break
      case OTHER:
        content = renderOther()
        break
      case COMING_BACK_LATER:
        content = null
        break
      default:
        break
    }

    return content
  }

  // Cancel Flow 1G: order Special Offer → Pause → FMO → Confirm (skip if not eligible)
  // Technical issues skips Special Offer only. Pause when not going to Special Offer (if eligible).
  const renderCancelButtons = () => {
    const requestCountry = user.getIn(['data', 'requestCountry'])
    const billingCountry = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'countryIso'])
    const isGermanUser = [requestCountry, billingCountry].includes(COUNTRY_DE)
    const specialOfferEligible = shouldRenderCancelPriceIncreaseOffer(props)
      && cancelReason !== TECHNICAL_ISSUES
    const showPauseModal = !currentPlanIsGift && userCanPause && !specialOfferEligible
    const noOffersEligible = !freeOfferEligible && !specialOfferEligible
    const aiReason = [UNSATISFIED_CONTENT, COMPLETED_WHAT_I_WANTED].includes(cancelReason)
    const showButtonAi = (userPrimaryLanguage === EN && aiReason)
      || showAiGuideFromTechIssues

    const standardFlowUrl = freeOfferEligible
      ? URL_ACCOUNT_CANCEL_OFFER
      : URL_ACCOUNT_CANCEL_CONFIRM
    const nextUrl = specialOfferEligible ? URL_ACCOUNT_CANCEL_SPECIAL_OFFER : standardFlowUrl

    const buttonText = showButtonAi ? staticText.get('iStillWantToCancel') : staticText.get('continue')
    const buttonType = showButtonAi ? TYPES.SECONDARY : TYPES.PRIMARY
    const buttonClass = showButtonAi ? 'account-cancel__button-next--ai' : ''

    const onClickContinue = () => {
      if (isGermanUser) {
        setUserAccountCancel()
        historyRedirect({
          history,
          url: URL_ACCOUNT_CANCEL_CONFIRM,
          auth,
          language: userLanguage,
        })
        return
      }

      if (showPauseModal) {
        renderModal(TYPE_MY_ACCOUNT_CANCEL_MEMBERSHIP, {
          url: nextUrl,
          user,
        })
        return
      }

      if (noOffersEligible) {
        setUserAccountCancel()
      }

      if (specialOfferEligible) {
        const skus = getCancelSpecialOfferSkusFor1g(userAccount)
        setUserAccountCancelSpecialOfferSkus(skus)
      }

      historyRedirect({
        history,
        url: nextUrl,
        auth,
        language: userLanguage,
      })
    }

    return (
      <div className="account-cancel__buttons-wrapper">
        <Button
          disabled={nextDisabled}
          type={buttonType}
          className={`account-cancel__button-next ${buttonClass}`}
          onClick={onClickContinue}
          submit
        >
          {buttonText}
        </Button>
      </div>
    )
  }

  // return nothing if not logged in
  if (!getAuthIsLoggedIn(auth)) {
    return null
  }

  // show processing state
  if (!dataReady) {
    return (
      <div className="account-cancel">
        <div className="account-cancel__content">
          <Sherpa type={TYPE_SMALL_WHITE} />
        </div>
      </div>
    )
  }

  // render if the user accepted the cancel offer on this page
  if (userPlanType !== PLAN_SUBSCRIPTION_MONTHLY && subscriptionSuccess) {
    return (
      <div className="account-cancel">
        <div className="account-cancel__content">
          <AccountCancelOffer />
        </div>
      </div>
    )
  }

  return (
    <div className="account-cancel">
      <div className="account-cancel__content">
        <H1 as={HEADING_TYPES.H3} className="account-cancel__title">
          {staticText.get('shareWhyLeaving')}
        </H1>
        <p className="account-cancel__text">
          {staticText.get('sadToSeeYouLeave')}
        </p>
        <FormsyForm
          className="account-cancel__form"
          onValidSubmit={onValidSubmit}
        >
          <Select
            label={''}
            value={cancelReason}
            name={CANCEL_REASON_SELECT}
            onChange={onChangeSelect}
            className="account-cancel__select"
          >
            <option value={''} key={'chooseOne'}>
              {`${staticText.get('chooseOne')}...`}
            </option>
            <option value={TOO_EXPENSIVE} key={TOO_EXPENSIVE}>
              {staticText.get('pricing')}
            </option>
            <option value={UNSATISFIED_CONTENT} key={UNSATISFIED_CONTENT}>
              {staticText.get('contentQuality')}
            </option>
            <option value={DONT_HAVE_TIME} key={DONT_HAVE_TIME}>
              {staticText.get('iDontHaveTime')}
            </option>
            <option value={COMPLETED_WHAT_I_WANTED} key={COMPLETED_WHAT_I_WANTED}>
              {staticText.get('completedWhatIWant')}
            </option>
            <option value={TECHNICAL_ISSUES} key={TECHNICAL_ISSUES}>
              {staticText.get('technicalDifficulties')}
            </option>
            <option value={OTHER} key={OTHER}>
              {staticText.get('other')}
            </option>
            <option value={COMING_BACK_LATER} key={COMING_BACK_LATER}>
              {staticText.get('planToComeBack')}
            </option>
          </Select>
          {renderBonusLossInfo()}
          {renderContent()}
          {
            subscriptionSuccess
            || subscriptionError
            || subscriptionProcessing
            || userAccountProcessing
              ? null
              : renderCancelButtons()
          }
        </FormsyForm>
      </div>
    </div>
  )
}

AccountCancelPage.propTypes = {
  history: PropTypes.object.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  plans: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  userLanguage: ImmutablePropTypes.list.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  subscription: ImmutablePropTypes.map.isRequired,
  cancelReason: PropTypes.string,
  setUserAccountCancelReason: PropTypes.func.isRequired,
  setUserAccountCancelOfferShown: PropTypes.func.isRequired,
  setUserAccountCancel: PropTypes.func.isRequired,
  setUserAccountCancelFormData: PropTypes.func.isRequired,
  setUserAccountCancelSpecialOfferSkus: PropTypes.func.isRequired,
}

export default compose(
  connectPage({ seoType: PAGE_SEO_TYPE_LOCATION }),
  connectRedux(
    (state) => ({
      auth: state.auth,
      user: state.user,
      plans: state.plans,
      userAccount: state.userAccount,
      staticText: state.staticText.getIn(['data', 'accountCancelPage', 'data']),
      userLanguage: state.user.getIn(['data', 'language'], List()),
      cancelReason: state.userAccount.getIn(['manageSubscription', 'data', 'cancelReason'], ''),
      subscription: state.subscription,
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setUserAccountCancelReason: actions.userAccount.setUserAccountCancelReason,
        setUserAccountCancelOfferShown: actions.userAccount.setUserAccountCancelOfferShown,
        setUserAccountCancel: actions.userAccount.setUserAccountCancel,
        setUserAccountCancelFormData: actions.userAccount.setUserAccountCancelFormData,
        setUserAccountCancelSpecialOfferSkus:
          actions.userAccount.setUserAccountCancelSpecialOfferSkus,
        setEventDefault: actions.eventTracking.setEventDefault,
        renderModal: actions.dialog.renderModal,
      }
    },
  ),
)(AccountCancelPage)
